import {
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  updateUserAttributes,
  updatePassword,
  sendUserAttributeVerificationCode,
  confirmUserAttribute,
  rememberDevice,
  resetPassword,
  confirmResetPassword,
  updateMFAPreference
} from 'aws-amplify/auth'
import {computed, makeObservable, observable} from "mobx";
import * as APITypes from "../API";
import {UpdateUserInput, UserRole, UserType} from "../API";
import User from "../model/User";
import RbcAPI from "../apis/RbcAPI";
import Logger from "../components/Logger";
import {differenceInCalendarDays, startOfToday} from "date-fns";
import Tracking from "../components/Tracking";
import {getISODateFromDate, getISODateTime, getISODateToday, isoToLocalDate, phoneToE164Format} from "./StoreUtilities";
import BillingAPI from "../apis/BillingAPI";
import IUserItem from "../model/IUserItem";
import UserSetting from "../model/UserSetting";
import Review from "../model/Review";
import ConvertKitAPI, {ConvertKitTag} from "../apis/ConvertKitAPI";

export const UserStoreConstants = {

  USER_ALREADY_CONFIRMED: "User cannot be confirm. Current status is CONFIRMED",
  CONFIRMATION_SUCCESS: "SUCCESS",
  USER_NOT_FOUND: "User not found",
  USER_DOES_NOT_EXIST: "User does not exist",
  USER_ALREADY_EXISTS: "User already exists",
  USERNAME_EXISTS_EXCEPTION: "UsernameExistsException",
  EMAIL_EXISTS_MESSAGE: "PreSignUp failed with error Email exists.",
  PHONE_EXISTS_MESSAGE: "PreSignUp failed with error Phone exists",
  USERNAME_NOT_CONFIRMED_EXCEPTION: "UserNotConfirmedException",
  USER_NOT_CONFIRMED: "User not confirmed",
  NOT_AUTHORIZED_EXCEPTION: "NotAuthorizedException",
  USER_NOT_FOUND_EXCEPTION: "UserNotFoundException", // Deprecated
  CODE_MISMATCH_EXEPTION: "CodeMismatchException",
  CONDITIONAL_REQUEST_FAILED: "The conditional request failed",
  EMAIL_VERIFICATION_PENDING: "Email verification pending",
  PHONE_VERIFICATION_PENDING: "Phone verification pending",
  COMPANY_EMAIL: "",
  PRO_TRIAL_DAYS: 14
}

export interface ICognitoAttributes {
  email?: string,
  phone_number?: string,
  "custom:account"?: string,
  "custom:role"?: string
  [key: string]: string | undefined
}

export const CognitoAttribute = {
  EMAIL: "email",
  PHONE_NUMBER: "phone_number",
  ACCOUNT: "custom:account",
  ROLE: "custom:role"
}

export const CognitoAttributeValue = {
  FALSE: "false",
  TRUE: "true"
}

export const ReferrerOptions: string[] = [
  "Direct (no-referrer)",
  "AI (ChatGPT, Gemini, etc)",
  "Email / Book from Jason Parker",
  "Facebook",
  "Friend or family",
  "Google",
  "Instagram",
  "Parker Financial",
  "Podcast - Sound Retirement Radio",
  "Podcast - Other",
  "Reddit",
  "TikTok",
  "Web Search",
  "X (Twitter)",
  "YouTube",
  "Other",
]

export enum ReferrerIndex {
  Direct,
  AI,
  EmailBook,
  Facebook,
  FriendFamily,
  Google,
  Instagram,
  ParkerFinancial,
  PodcastSRR ,
  PodcastOther,
  Reddit,
  TikTok,
  WebSearch,
  XTwitter,
  YouTube,
  Other,
}

class UserStore {
  @observable user?: User
  @observable isLoading: boolean = false
  @observable isAdmin: boolean = false

  rbcAPI: RbcAPI
  billingAPI: BillingAPI
  convertKitAPI: ConvertKitAPI

  @computed get isAdvisor() {
    return this.user !== undefined ? this.user.isAdvisor : false
  }

  @computed get isAdvisorOrAdmin() {
    return this.user !== undefined ? this.user.isAdvisor || this.isAdmin : false
  }

  @computed get isCustomer() {
    return this.user !== undefined ? this.user.isCustomer : false
  }

  @computed get isFree() : boolean {
    return (this.user !== undefined && this.user.userType === UserType.Free)
  }

  @observable private _cognitoUser?: any
  get cognitoUser() {
    // Logger.debug(`UserStore get cognitoUser = ${this._cognitoUser?.username}`)
    return this._cognitoUser
  }

  set cognitoUser(cognitoUser: any | undefined) {
    // Logger.debug(`UserStore set cognitoUser = ${cognitoUser?.username}`)
    this._cognitoUser = cognitoUser
  }

  private _attributes: any[] = []

  constructor(options: any) {
    makeObservable(this);
    this.rbcAPI = (options && options.rbcAPI) ? options.rbcAPI : null
    this.billingAPI = (options && options.billingAPI) ? options.billingAPI : null
    this.convertKitAPI = (options && options.convertKitAPI) ? options.convertKitAPI : null
  }

  async signOutCurrentAuthenticatedUser() {
    try {
      await getCurrentUser()
      // Logger.debug('Current authenticated user', user)
      await signOut()
    } catch (err) {
      // If we see err === "No current user," then we are not logged in, which is fine.
      // Logger.debug('UserStore.signIn: ', err)
    } finally {
      // this.deleteAllCookies()
    }
  }

  signUp = async (username: string, password: string, email: string, phone?: string, accountId?: string, role?: UserRole) => {
    this.cognitoUser = undefined
    this.user = undefined
    this._attributes = []

    await this.signOutCurrentAuthenticatedUser()

    const userAttributes: Record<string, string> = {}
    if (email) {
      userAttributes[CognitoAttribute.EMAIL] = email.toLowerCase()
    }
    if (phone) {
      const formattedPhone = phoneToE164Format(phone)
      if (formattedPhone) {
        userAttributes[CognitoAttribute.PHONE_NUMBER] = formattedPhone
      }
    }
    if (accountId) {
      userAttributes[CognitoAttribute.ACCOUNT] = accountId
    }
    if (role) {
      userAttributes[CognitoAttribute.ROLE] = role
    }

    const { isSignUpComplete, userId, nextStep } = await signUp({
      username,
      password,
      options: {
        userAttributes
      }
    })
    return { isSignUpComplete, userId, nextStep }
  }

  confirmSignUp = async (username: string, code: string, options?: any): Promise<any> => {
    return await confirmSignUp({ username, confirmationCode: code })
  }

  resendSignUp = async (username: string): Promise<any> => {
    return await resendSignUpCode({ username })
  }

  verifyUserAttribute = async (user: any, attribute: 'email' | 'phone_number') => {
    return await sendUserAttributeVerificationCode({ userAttributeKey: attribute })
  }

  verifyUserAttributeSubmit = async (user: any, attribute: 'email' | 'phone_number', code: string) => {
    return await confirmUserAttribute({ userAttributeKey: attribute, confirmationCode: code })
  }

  signIn = async (email: string, password: string) => {
    this.cognitoUser = undefined
    this.user = undefined
    this._attributes = []

    const username = email.toLowerCase()

    await this.signOutCurrentAuthenticatedUser()

    // Turn a signIn result into either the next-step output (e.g. MFA / new
    // password challenge) or the initialized User.
    const handleSignInOutput = async (signInOutput: any) => {
      const { isSignedIn, nextStep } = signInOutput
      if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' || nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
        return signInOutput
      } else if (isSignedIn) {
        const currentUser = await getCurrentUser()
        return await this.initSession(currentUser)
      }
      return signInOutput
    }

    try {
      const signInOutput = await signIn({ username, password })
      return await handleSignInOutput(signInOutput)
    } catch (err: any) {
      // Not-yet-migrated users don't exist in the new pool. With
      // preventUserExistenceErrors enabled (the Cognito default), SRP surfaces
      // this as NotAuthorizedException rather than UserNotFoundException; both
      // mean "retry with USER_PASSWORD_AUTH", the only flow that fires the
      // Cognito user-migration trigger (it needs the plaintext password — SRP
      // never fires it). A wrong password for an already-migrated user also
      // lands here and simply fails the retry the same way, which is fine.
      if (err.name === UserStoreConstants.USER_NOT_FOUND_EXCEPTION ||
          err.name === UserStoreConstants.NOT_AUTHORIZED_EXCEPTION) {
        try {
          const migratedOutput = await signIn({ username, password, options: { authFlowType: 'USER_PASSWORD_AUTH' } })
          return await handleSignInOutput(migratedOutput)
        } catch (migrateErr: any) {
          if (migrateErr.name !== UserStoreConstants.USER_NOT_FOUND_EXCEPTION &&
            migrateErr.name !== UserStoreConstants.NOT_AUTHORIZED_EXCEPTION) {
            Logger.error("Auth.SignIn error.", migrateErr)
          }
          throw migrateErr
        }
      }
      if (err.name !== UserStoreConstants.NOT_AUTHORIZED_EXCEPTION) {
        Logger.error("Auth.SignIn error.", err)
      }
      throw err
    }
  }

  confirmSignIn = async (authUser: any, code: string, mfaType: "SMS_MFA" | "SOFTWARE_TOKEN_MFA")=> {
    const { isSignedIn } = await confirmSignIn({ challengeResponse: code })
    if (isSignedIn) {
      const currentUser = await getCurrentUser()
      const user = await this.initSession(currentUser)
      return user
    }
  }

  rememberDevice = async () => {
    await rememberDevice()
  }

  reloadAuthenticatedUser = async (): Promise<User | null | undefined> => {
    const currentUser = await this.currentAuthenticatedUser()
    if (currentUser) {
      console.log("reloading current authenticated user")
      this.isLoading = true
      // Load and initialize User
      return this.initSession(currentUser)
        .then(async (result: any) => {
          console.log("Reloaded user from cache")
          return this.user
        })
        .catch(async (reason: any) => {
          this.isLoading = false
          console.log("Unable to load user from cache")
          return null
        })
    } else {
      console.log("No existing authenticated user found")
      this.isLoading = false
      return null
    }

  }

  signOut = async () => {
    return new Promise<any>((resolve, reject) => {
      this.currentAuthenticatedUser()
        .then((currentUser: any) => {
          signOut()
            .then(() => {
              // Logger.debug("Auth.signOut success")
              this.cognitoUser = undefined
              this.user = undefined
              this.isAdmin = false
              this._attributes = []
              if (this.checkInterval) {
                clearInterval(this.checkInterval)
                this.checkInterval = undefined
              }
              resolve(null)
            })
            .catch(err => {
              Logger.error("Auth.signOut error", err)
              reject(err)
            })
          // .finally(() => {
          //   this.deleteAllCookies()
          // })
        })
    })
  }

  currentSession = async () => {
    fetchAuthSession()
      .then(data => {
        return data
      })
      .catch(err => {
        console.log(`fetchAuthSession err: ${JSON.stringify(err)}`)
      });
  }

  currentAuthenticatedUser = async () => {
    const currentUser = await getCurrentUser()
      .catch(err => {
        this.cognitoUser = undefined
      })
    if (currentUser) {
      // TODO: Disable logging after testing
      this.logCurrentSession()
      this.cognitoUser = currentUser as any
      return currentUser
    } else {
      this.cognitoUser = undefined
      return null
    }
  }

  logCurrentSession = async () => {
    const session = await fetchAuthSession()
    if (session && session.tokens) {
      const idToken = session.tokens.idToken
      if (idToken) {
        const idPayload = idToken.payload
        if (idPayload.iat) {
          let issuedDate = new Date(idPayload.iat * 1000)
          console.debug(`idToken issuedAt: ${issuedDate.toLocaleString()}`)
        }
        if (idPayload.exp) {
          let expirationDate = new Date(idPayload.exp * 1000)
          console.debug(`idToken expiration: ${expirationDate.toLocaleString()}`)
        }
      }

      const accessToken = session.tokens.accessToken
      if (accessToken) {
        const accessPayload = accessToken.payload
        if (accessPayload.iat) {
          let issuedDate = new Date(accessPayload.iat * 1000)
          console.debug(`accessToken issuedAt: ${issuedDate.toLocaleString()}`)
        }
        if (accessPayload.exp) {
          let expirationDate = new Date(accessPayload.exp * 1000)
          console.debug(`accessToken expiration: ${expirationDate.toLocaleString()}`)
        }
      }
    }
  }

  getUserAttribute = async (cognitoUser: any, name: string) => {
    const attributes = await fetchUserAttributes()
    return attributes[name] ?? null
  }

  getUserAttributes = async (cognitoUser?: any) => {
    return await fetchUserAttributes()
  }

  updateUserAttributes = async (cognitoUser: any, attributes: any) => {
    return await updateUserAttributes({ userAttributes: attributes })
  }


  initSession = async (currentUser: any) => {

    console.log("UserStore.initSession")
    this.isLoading = true

    return new Promise<any>(async (resolve, reject) => {
      this.cognitoUser = currentUser
      await this.getCurrentSessionPayload()
      const username = currentUser.username
      await this.checkAuthentication()

      // Resolve the User record by Cognito username, which equals User.id for
      // both native and migrated users (see user_migration_rbcid.md). This
      // used to read a custom rbcId claim off the ID token instead; that
      // claim has been retired since username alone is always correct now.
      this.loadUser(username)
        .then(async (user: any) => {
          await this.verifyRole(user)
          await this.verifyAccount(user)
          await this.verifySubscription(user)
          Tracking.setUserId(user.id)
          this.isLoading = false
          resolve(user)
        })
        .catch((err: any) => {
          this.isLoading = false
          reject(err)
        })
    })
  }

  updateLastAccess = async (user: User)=> {
    const input: UpdateUserInput = {
      id: user.id,
      lastAccess: getISODateTime()
    }
    if (!user.sessionCount) {
      input.sessionCount = 1
    } else if (!user.lastAccess || !user.lastAccess.startsWith(getISODateToday())) {
      input.sessionCount = user.sessionCount + 1
    }
    await this.updateUser(input)
  }

  async verifyRole(user: User) {
    // Verify the cognito role matches the User record role and update if needed
    const attributes = await this.getUserAttributes(this.cognitoUser)
    const updateAttributes: ICognitoAttributes = {}

    const role = user.role
    const customRole = attributes[CognitoAttribute.ROLE]
    if (!customRole || customRole !== role) {
      updateAttributes[CognitoAttribute.ROLE] = role
      console.log(`Updating ${user.email} role to ${role}`)
      await this.updateUserAttributes(null, updateAttributes)
    }
  }

  async verifyAccount(user: User) {
    // Verify the cognito role matches the User record role and update if needed
    const attributes: any = await this.getUserAttributes(this.cognitoUser)
    const updateAttributes: ICognitoAttributes = {}

    const accountId = user.accountId

    const customAccount = attributes[CognitoAttribute.ACCOUNT]
    if (!customAccount || customAccount !== accountId) {
      updateAttributes[CognitoAttribute.ACCOUNT] = accountId
      console.log(`Updating ${user.email} account to ${accountId}`)
      await this.updateUserAttributes(null, updateAttributes)
    }
  }

  async verifySubscription(user: User) {
    if ((user.userType === UserType.Premium || user.userType === UserType.Free) && user.customerId) {
      try {
        let subscription
        const customer = await this.billingAPI.getCustomer(user.customerId)
        if (customer) {
          if (customer["subscriptions"] && customer["subscriptions"]["data"].length > 0) {
            subscription = customer["subscriptions"]["data"][0]
          }
        }
        if (!subscription || subscription["status"] !== "active") {
          if (user.userType !== UserType.Free) {
            // Switch to Free
            const input: UpdateUserInput = {
              id: user.id,
              userType: UserType.Free
            }
            await this.updateUser(input)
            this.convertKitAPI.addTag(ConvertKitTag.FreeSubscription, user.email)
          }
        } else {
          if (user.userType === UserType.Free) {
            // Switch to Premium
            const input: UpdateUserInput = {
              id: user.id,
              userType: UserType.Premium
            }
            await this.updateUser(input)
          }
        }
      } catch (err: any) {
        console.log(`Error checking subscripton`, err.message)
      }
    } else if (user.userType === UserType.Trial) {
      const trialDaysRemaining = this.getTrialDaysRemaining()
      if (trialDaysRemaining !== undefined && trialDaysRemaining < 0) {
        // Switch to Free
        const input: UpdateUserInput = {
          id: user.id,
          userType: UserType.Free
        }
        await this.updateUser(input)
        this.convertKitAPI.addTag(ConvertKitTag.FreeSubscription, user.email)
      }
    }
  }

  async getCurrentSessionPayload() {
    const session = await fetchAuthSession()
    // const authTimeValue = session.tokens?.idToken?.payload['auth_time']
    // const authTime = fromUnixTime(authTimeValue)
    // Logger.debug(`User authenticated at ${format(authTime!, "M/d/yyyy h:mm:ss aa")}`)
    const groups = session.tokens?.idToken?.payload['cognito:groups'] as string[] | undefined
    this.isAdmin = (groups !== undefined && groups.indexOf("Admin") >= 0)
    return groups
  }

  @computed get isAuthenticated() {
    const isAuthenticated = this.cognitoUser !== undefined && this.user !== undefined
    // Logger.debug(`UserStore get isAuthenticated = ${isAuthenticated}`)
    return isAuthenticated
  }

  checkInterval: any

  async checkAuthentication() {
    return
    /* Disable authentication check for now
    Logger.debug("checkAuthentication")
    try {
      const session = await Auth.currentSession()
      const authTimeValue = session.getIdToken().payload['auth_time']
      const authTime = fromUnixTime(authTimeValue)

      if (!isToday(authTime)) {  // Sign-out at midnight the day authenticated
        Logger.debug("Authentication expired")
        await this.signOut()
        // Route to home page
        navigate(RoutesConfig.signIn.pathname!)
        // window.location.reload() // necessary for reconfiguring app state after logout
      }
    } catch (err) {
      Logger.error('checkAuthentication error', err)
    }

    if (!this.checkInterval) {
      // Check authentication every hour
      this.checkInterval = setInterval(() => this.checkAuthentication(), 60 * 60000)
    }
  */
  }

  completeNewPassword = async (newPassword: string) => {
    return await new Promise<any>((resolve, reject) => {
      confirmSignIn({ challengeResponse: newPassword })
        .then(data => {
          Logger.debug("confirmSignIn (new password) success")
          resolve(data)
        })
        .catch(err => {
          Logger.debug("confirmSignIn (new password) error", err)
          reject(err)
        });
    })
  }

  loadUser = async (userId: string): Promise<User | undefined> => {
    Logger.debug(`UserStore.loadUser(${userId})`)

    const data = await this.rbcAPI.getUser(userId)
    console.log("Loaded user")

    if (data) {
      let user = new User(data)
      if (user) {
        this.user = user
        if (user.role === UserRole.Admin && !this.isAdmin) {
          user.role = UserRole.Advisor
        }
        console.log("Initializing Logger")
        Logger.configureUser(this.user.id)
        Logger.debug("Signed in as " + this.user.id)
        console.log("loadUser completed")
      }
    } else {
      throw new Error("User not found")
    }

    return this.user
  }

  getUser = async (userId: string): Promise<User | undefined> => {
    const data = await this.rbcAPI.getUser(userId)
    if (data) {
      return new User(data)
    } else {
      return undefined
    }
  }

  getUserOnly = async (userId: string): Promise<User | undefined> => {
    const data = await this.rbcAPI.getUserOnly(userId)
    if (data) {
      return new User(data)
    } else {
      return undefined
    }
  }

  async createUser(input: APITypes.CreateUserInput, setStoreUser: boolean = false) {
    if (input.phone) {
      input.phone = phoneToE164Format(input.phone)
    }
    const userRes = await this.rbcAPI!.createUser(input)
    if (userRes) {
      Tracking.event({ action: "Create Account" })
      //   const attributes: ICognitoAttributes = {}
      //   let updateAttributes = false
      //   if (user.accountId) {
      //     attributes[CognitoAttribute.ACCOUNT] = user.accountId
      //     updateAttributes = true
      //   }
      //   if (user.role) {
      //     attributes[CognitoAttribute.ROLE] = user.role
      //     updateAttributes = true
      //   }
      //   if (updateAttributes) {
      //     await this.updateUserAttributes(null, attributes)
      //   }

      const user = new User(userRes)
      if (setStoreUser) this.user = user
      // this.createActivity(ActivityType.UserCreate, this.user.id)
      return user
    } else {
      return null
    }
  }

  async updateUser(input: APITypes.UpdateUserInput) {

    if (input.phone) {
      input.phone = phoneToE164Format(input.phone)
    }
    const user = await this.rbcAPI!.updateUser(input)
    if (user) {
      const updatedUser = new User(user)

      if (this.user && user.id === this.user!.id) {
        // Verify custom account user attribute.  Only works for current authenticated user
        const accountValue = await this.getUserAttribute(null, CognitoAttribute.ACCOUNT)
        if (accountValue !== user.accountId) {
          const attributes: ICognitoAttributes = {}
          attributes[CognitoAttribute.ACCOUNT] = user.accountId
          console.log(`Updated custom:Account Cognito attribute: ${user.accountId}`)
          await this.updateUserAttributes(null, attributes)
        }
        const roleValue = await this.getUserAttribute(null, CognitoAttribute.ROLE)
        if (roleValue !== user.role) {
          const attributes: ICognitoAttributes = {}
          attributes[CognitoAttribute.ROLE] = user.role
          console.log(`Updated custom:Role Cognito attribute: ${user.role}`)
          await this.updateUserAttributes(null, attributes)
        }

        if (user.id === this.user!.id) {
          // Preserve related data
          updatedUser.account = this.user!.account
          updatedUser.models = this.user!.models
          updatedUser.reviews = this.user!.reviews
          updatedUser.settings = this.user!.settings
          this.user = updatedUser
        }
      }

      return updatedUser
    } else {
      return null
    }
  }
  
  // UserSetting

  getUserSetting = async (userSettingId: string): Promise<UserSetting | undefined> => {
    const data = await this.rbcAPI.getUserSetting(userSettingId)
    if (data) {
      return new UserSetting(data)
    } else {
      return undefined
    }
  }

  getTrialDaysRemaining = () : number | undefined => {
    let days : number | undefined = undefined
    let user = this.user
    if (user && user.trialEndDate) {
      const now = startOfToday()
      const trialEndDate = isoToLocalDate(user.trialEndDate)
      days = differenceInCalendarDays(trialEndDate, now)
    }

    return days
  }

  async createUserSetting(input: APITypes.CreateUserSettingInput) {
    if (input.id === "current") {
      throw new Error("Cannot save current userSetting")
    }
    const data = await this.rbcAPI!.createUserSetting(input)
    if (data) {
      const userSetting = new UserSetting(data)
      this.updateUserItem("settings", userSetting)
      return userSetting
    } else {
      return undefined
    }
  }

  async updateUserSetting(input: APITypes.UpdateUserSettingInput) {
    const data = await this.rbcAPI!.updateUserSetting(input)
    if (data) {
      const userSetting = new UserSetting(data)
      this.updateUserItem("settings", userSetting)
      return userSetting
    } else {
      return undefined
    }
  }

  deleteUserSetting = async (userSettingId: string): Promise<UserSetting | undefined> => {
    const data = await this.rbcAPI.deleteUserSetting(userSettingId)
    if (data) {
      const userSetting = new UserSetting(data)
      this.deleteUserItem("settings", userSetting)
      return userSetting
    } else {
      return undefined
    }
  }

  updateUserItem (list: string, userItem: IUserItem, sortFn?: (array: any[]) => void) {
    if (this.user && this.user.id === userItem.userId) {
      let array = (this.user as any)[list]
      if (array) {
        const index = array.findIndex((item: any) => item["id"] === userItem.id)
        if (index >= 0) {
          array[index] = userItem
        } else {
          array.push(userItem)
        }
        if (sortFn) {
          sortFn(array)
        }
      }
    }
  }

  deleteUserItem(list: string, userItem: IUserItem) {
    if (this.user && this.user.id === userItem.userId) {
      let array = (this.user as any)[list]
      if (array) {
        const index = array.findIndex((item: any) => item["id"] === userItem.id)
        if (index >= 0) {
          array.splice(index, 1)
        }
      }
    }
  }


  async updateCancelDate(user: User, cancelDate: Date | null) {
    const cancelledOn = cancelDate ? getISODateFromDate(cancelDate) : null

    if (cancelledOn !== user.cancelledOn) {
      const input: UpdateUserInput = {
        id: user.id,
        cancelledOn: cancelledOn
      }
      await this.updateUser(input)
      return (true)
    } else {
      return (false)
    }
  }
  
  // Review

  getReview = async (reviewId: string): Promise<Review | undefined> => {
    const data = await this.rbcAPI.getReview(reviewId)
    if (data) {
      return new Review(data)
    } else {
      return undefined
    }
  }

  async createReview(input: APITypes.CreateReviewInput) {
    const data = await this.rbcAPI!.createReview(input)
    if (data) {
      const review = new Review(data)
      this.updateUserItem("reviews", review, User.sortReviews)
      return review
    } else {
      return undefined
    }
  }

  async updateReview(input: APITypes.UpdateReviewInput) {
    const data = await this.rbcAPI!.updateReview(input)
    if (data) {
      const review = new Review(data)
      this.updateUserItem("reviews", review, User.sortReviews)
      return review
    } else {
      return undefined
    }
  }

  deleteReview = async (reviewId: string): Promise<Review | undefined> => {
    const data = await this.rbcAPI.deleteReview(reviewId)
    if (data) {
      const review = new Review(data)
      this.deleteUserItem("reviews", review)
      return review
    } else {
      return undefined
    }
  }

  async forgotPassword(userId: string) {
    return await resetPassword({ username: userId })
  }

  async forgotPasswordSubmit(userId: string, code: string, password: string) {
    return await confirmResetPassword({ username: userId, confirmationCode: code, newPassword: password })
  }

}

export default UserStore